/**
 * Change PIN — the signed-in user's self-service login-PIN change (web port).
 *
 * Web counterpart of apps/mobile/app/change-pin.tsx. For a user who knows their
 * current PIN and is already logged in: prove the current PIN, set a new one
 * (with confirm), done. No OTP, no token rotation — a single authenticated
 * POST /me/change-pin.
 *
 *   current PIN -> new PIN -> confirm new PIN -> Change PIN
 *
 * Client validation mirrors the mobile screen (4-8 digits, new === confirm,
 * new !== current) before the request fires. A wrong current PIN comes back 401
 * and is mapped to a friendly message via FseApiError.status; a 400 surfaces the
 * server message (or a "no PIN set" fallback). On success we show a success
 * Alert and clear all three fields.
 *
 * Contract verified against services/api/src/me/dto.ts via the manual client
 * src/api/manual/me-security.ts (validate-API-before-smoke rule).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Text,
  TextField,
} from '@fse/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FseApiError } from '../api/client';
import { meControllerChangePin } from '../api/generated/me/me';

// 4-8 digit numeric PIN (leading zeros allowed) — mirrors ChangePinSchema.
const PIN_RE = /^\d{4,8}$/;

// Client-side validation outcome codes. Kept as a stable enum (rather than the
// translated message) so logic can compare against them irrespective of locale
// (TD-088). Mapped to copy via the changePin.validation.* keys at render.
type PinValidationCode = 'format' | 'mismatch' | 'unchanged';

export function ChangePinScreen() {
  const { t } = useTranslation();
  const [oldPin, setOldPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  // Client-side validation error code (pre-submit). null when the form is valid.
  const [validationError, setValidationError] = useState<PinValidationCode | null>(null);

  const m = useMutation({
    mutationFn: () => meControllerChangePin({ oldPin, newPin }),
    onSuccess: () => {
      // Clear the form so a stale PIN can't be resubmitted or shoulder-surfed.
      setOldPin('');
      setNewPin('');
      setConfirmPin('');
      setValidationError(null);
    },
  });

  const isSaving = m.isPending;

  function onSubmit() {
    setValidationError(null);

    if (!PIN_RE.test(newPin)) {
      setValidationError('format');
      return;
    }
    if (newPin !== confirmPin) {
      setValidationError('mismatch');
      return;
    }
    if (newPin === oldPin) {
      setValidationError('unchanged');
      return;
    }

    m.mutate();
  }

  // Resolve a validation code to its localized message.
  function validationMessage(code: PinValidationCode): string {
    if (code === 'format') return t('changePin.validation.format');
    if (code === 'mismatch') return t('changePin.validation.mismatch');
    return t('changePin.validation.unchanged');
  }

  // Map a failed request to a friendly message. 401 = wrong current PIN;
  // 400 = validation / account has no PIN set (prefer the server message).
  function apiErrorMessage(err: unknown): string {
    if (err instanceof FseApiError) {
      if (err.status === 401) return t('changePin.error.wrongPin');
      if (err.status === 400) {
        return err.message || t('changePin.error.noPin');
      }
      return err.message;
    }
    return t('changePin.error.generic');
  }

  // The new-PIN field is invalid only after a failed validation attempt, so the
  // helperText stays a hint until the user actually submits a bad value.
  const newPinFormatInvalid = validationError === 'format';

  return (
    <Stack spacing={3} sx={{ maxWidth: 560 }}>
      <Box>
        <Text variant="headline-medium" as="h1">
          {t('changePin.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('changePin.subtitle')}
          </Text>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Text variant="title-medium" as="h2">
              {t('changePin.cardTitle')}
            </Text>
          </Box>
          <Stack spacing={3}>
            {/* Current PIN */}
            <Box>
              <Box sx={{ mb: 1 }}>
                <Text variant="label-large" color="text.primary">
                  {t('changePin.currentPin')}
                </Text>
              </Box>
              <TextField
                type="password"
                inputMode="numeric"
                autoComplete="current-password"
                value={oldPin}
                onChange={(e) => setOldPin(e.target.value)}
                disabled={isSaving}
                aria-label={t('changePin.currentPin')}
                fullWidth
              />
            </Box>

            {/* New PIN */}
            <Box>
              <Box sx={{ mb: 1 }}>
                <Text variant="label-large" color="text.primary">
                  {t('changePin.newPin')}
                </Text>
              </Box>
              <TextField
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                error={newPinFormatInvalid}
                disabled={isSaving}
                aria-label={t('changePin.newPin')}
                fullWidth
              />
              <Box sx={{ mt: 0.75 }}>
                <Text variant="body-small" color="text.secondary">
                  {t('changePin.pinHint')}
                </Text>
              </Box>
            </Box>

            {/* Confirm new PIN */}
            <Box>
              <Box sx={{ mb: 1 }}>
                <Text variant="label-large" color="text.primary">
                  {t('changePin.confirmPin')}
                </Text>
              </Box>
              <TextField
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value)}
                disabled={isSaving}
                aria-label={t('changePin.confirmPin')}
                fullWidth
              />
            </Box>

            {validationError ? (
              <Alert severity="error">{validationMessage(validationError)}</Alert>
            ) : null}

            {m.isError ? (
              <Alert severity="error">{apiErrorMessage(m.error)}</Alert>
            ) : null}

            {m.isSuccess ? (
              <Alert severity="success">{t('changePin.success')}</Alert>
            ) : null}

            <Box>
              <Button variant="contained" onClick={onSubmit} disabled={isSaving}>
                {isSaving ? t('changePin.changing') : t('changePin.submit')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
